/* eslint-disable react/prop-types, no-nested-ternary, jsx-a11y/no-autofocus */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { CloseIcon } from '../CommonUI';
import * as employeeServiceApi from '../../../../store/apis/employeeService';
import { resolveEmployeeImageSrc } from '../../../../utils/imageFile.util';

/**
 * Enhanced Search Modal following the modern design pattern.
 * Features a real-time search with a loader and formatted result cards.
 */
function SearchModal({ onPick, onClose }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  const handleSearch = useCallback(async val => {
    setLoading(true);
    try {
      const entityCode =
        localStorage.getItem('entityCode') ||
        sessionStorage.getItem('entityCode') ||
        '';
      const data = await employeeServiceApi.fetchEmployeeMasterSearch({
        prefixText: val,
        companyCode: entityCode,
      });
      // Ensure data is an array
      setResults(Array.isArray(data) ? data : []);
    } catch (err) {
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!query.trim()) {
      setResults([]);
      return;
    }

    const timer = setTimeout(() => {
      handleSearch(query);
    }, 400);

    return () => clearTimeout(timer);
  }, [query, handleSearch]);

  const getStatusLabel = status => {
    const s = String(status ?? '')
      .trim()
      .toUpperCase();
    if (['Y', '1', 'TRUE', 'ACTIVE'].includes(s)) return 'Active';
    if (['N', '0', 'FALSE', 'INACTIVE'].includes(s)) return 'Inactive';
    return '—';
  };

  const getStatusBadgeClass = status => {
    const s = String(status ?? '')
      .trim()
      .toUpperCase();
    if (['Y', '1', 'TRUE', 'ACTIVE'].includes(s)) return 'bg';
    if (['N', '0', 'FALSE', 'INACTIVE'].includes(s)) return 'br';
    return 'by';
  };

  return (
    <div className="ov">
      <div className="mdl" onClick={e => e.stopPropagation()}>
        <div className="mdl-top">
          <h2>Search Employee</h2>
          <button type="button" className="mdl-x" onClick={onClose}>
            <CloseIcon />
          </button>
        </div>

        <div className="mdl-sb">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
            <circle cx="11" cy="11" r="7" stroke="#94a3b8" strokeWidth="2" />
            <path
              d="m20 20-3.5-3.5"
              stroke="#94a3b8"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
          <input
            ref={inputRef}
            type="text"
            placeholder="Search by Employee Name & ID"
            value={query}
            onChange={e => setQuery(e.target.value)}
            onBlur={() => {
              // Keep focus on the input field
              setTimeout(() => {
                if (inputRef.current) {
                  inputRef.current.focus();
                }
              }, 0);
            }}
          />
          {/* {loading && (
            <div
              className="loader-spin"
              style={{ width: 18, height: 18, borderWidth: 2 }}
            />
          )} */}
        </div>

        <div className="mdl-body" style={{ minHeight: '300px' }}>
          {results.length > 0 ? (
            results.map((emp, i) => {
              const code = emp.EmpCd || emp.ID || emp.Cd || '';
              const name = emp.Name || emp.EmployeeName || '';
              const dept = emp.Department || emp.Dept || '';
              const loc = emp.Location || emp.LocCd || '';
              const activeVal =
                emp.Active ??
                emp.active ??
                emp.ActiveYN ??
                emp.activeYN ??
                emp.IsActive ??
                emp.isActive ??
                emp.Status ??
                '';
              const statusLabel = getStatusLabel(activeVal);
              const photo =
                emp.photo ||
                emp.Photo ||
                emp.Imagefile ||
                emp['Image File Path'] ||
                emp.FullImagePath;

              return (
                <div
                  key={code || i}
                  className="mdl-row anim-fade"
                  style={{ animationDelay: `${i * 0.05}s` }}
                  onClick={() => {
                    onPick({
                      empCode: code,
                      Post: emp.Post ?? emp.post,
                    });
                    onClose();
                  }}
                >
                  <div className="mdl-av">
                    {photo ? (
                      <img
                        src={resolveEmployeeImageSrc(photo)}
                        alt=""
                        style={{
                          width: '100%',
                          height: '100%',
                          borderRadius: '50%',
                          objectFit: 'contain',
                        }}
                      />
                    ) : (
                      <span>{name?.[0]}</span>
                    )}
                  </div>
                  <div className="mdl-info">
                    <div className="mdl-name">{name}</div>
                    <div className="mdl-meta">
                      {code} • {dept} • {loc}
                    </div>
                  </div>
                  <div
                    className={`mdl-badge ${getStatusBadgeClass(activeVal)}`}
                    title={`Status: ${String(activeVal ?? '')}`}
                  >
                    {statusLabel}
                  </div>
                </div>
              );
            })
          ) : query.length >= 2 && !loading ? (
            <div className="mdl-empty">
              No employees found matching &quot;{query}&quot;
            </div>
          ) : !loading ? (
            <div className="mdl-empty" style={{ opacity: 0.6 }}>
              Type at least 2 characters to search...
            </div>
          ) : null}

          {loading && results.length === 0 && (
            <div className="mdl-empty">
              <div
                className="loader-spin m-auto mb-3"
                style={{ width: 32, height: 32 }}
              />
              Searching records...
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default SearchModal;
